import type { TableState, ActiveBet } from '@casino/craps-engine';
import type { ZoneDef } from '../layout/zones.js';

function hopFacesEqual(
  a: readonly [number, number] | undefined,
  b: readonly [number, number] | undefined,
): boolean {
  if (a === undefined && b === undefined) return true;
  if (a === undefined || b === undefined) return false;
  return a[0] === b[0] && a[1] === b[1];
}

/**
 * Pick the bet that a long-press on `def` should remove (or undefined for none).
 * Filter mirrors the chip-stack grouping used at render time:
 *   - The COME / DONT_COME zones (`def.number === undefined`) target ONLY
 *     unsettled bets at that zone (`b.number === undefined`); a tap there must
 *     not reach into bets that have already travelled to a number, otherwise a
 *     player trying to peel the chips they just laid on COME would inadvertently
 *     start chipping away at their come-N stack (and trigger the engine's
 *     parent-cascade on its odds). Reported in feedback (2026-05-09).
 *   - Numbered zones (`def.number !== undefined`) match only bets at THAT
 *     number, just as today.
 *   - Hop zones key by `hopFaces` so each face-pair zone removes only its own.
 *
 * Among the matches, picks the most recently placed chip whose amount equals
 * the rack's selected denom — falls back to the most recent match if none of
 * them match the denom. This gives a tactile "remove a chip of the size you
 * have selected" UX without hard-failing if the denom changed since placement.
 */
export function pickLongPressTarget(
  state: TableState,
  def: ZoneDef,
  denom: number | undefined,
): ActiveBet | undefined {
  const matching = state.bets.filter((b) =>
    b.type === def.type
    && (def.number === undefined ? b.number === undefined : b.number === def.number)
    && hopFacesEqual(b.hopFaces, def.hopFaces)
  );
  if (matching.length === 0) return undefined;
  if (denom !== undefined) {
    for (let i = matching.length - 1; i >= 0; i--) {
      if (matching[i]!.amount === denom) return matching[i];
    }
  }
  return matching[matching.length - 1];
}
